import { OverlayLayerContext, useOverlayLayer } from "./OverlayLayer";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "../lib/cn";
import { Dialog, dialogBackdropClassName } from "./Dialog";

const sheetVariants = cva(
  "fixed z-modal flex flex-col gap-4 border-border bg-surface-raised p-5 pt-[max(1.25rem,env(safe-area-inset-top,0px))] pr-[max(1.25rem,env(safe-area-inset-right,0px))] pb-[max(1.25rem,env(safe-area-inset-bottom,0px))] pl-[max(1.25rem,env(safe-area-inset-left,0px))] text-fg shadow-lg outline-none transition-[opacity,translate] duration-slow ease-enter data-starting-style:opacity-0 data-ending-style:opacity-0",
  {
    variants: {
      side: {
        right:
          "inset-y-0 right-0 w-80 max-w-[90vw] border-l data-starting-style:translate-x-full data-ending-style:translate-x-full",
        left: "inset-y-0 left-0 w-80 max-w-[90vw] border-r data-starting-style:-translate-x-full data-ending-style:-translate-x-full",
        bottom:
          "inset-x-0 bottom-0 max-h-[85dvh] rounded-t-lg border-t data-starting-style:translate-y-full data-ending-style:translate-y-full",
        top: "inset-x-0 top-0 max-h-[85dvh] rounded-b-lg border-b data-starting-style:-translate-y-full data-ending-style:-translate-y-full",
      },
    },
    defaultVariants: { side: "right" },
  },
);

export interface SheetContentProps
  extends Omit<BaseDialog.Popup.Props, "className">, VariantProps<typeof sheetVariants> {
  className?: string;
  showClose?: boolean;
}

function Content({ className, side, children, showClose = true, ...props }: SheetContentProps) {
  const layer = useOverlayLayer("modal");
  return (
    <OverlayLayerContext value={layer}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          style={{ zIndex: `calc(${layer} - 1)` }}
          data-slot="sheet-backdrop"
          className={dialogBackdropClassName}
        />
        <BaseDialog.Popup
          data-slot="sheet-content"
          className={cn(sheetVariants({ side }), className)}
          {...props}
          style={{ zIndex: layer, ...props.style }}
        >
          {children}
          {showClose && (
            <BaseDialog.Close
              data-slot="sheet-close"
              aria-label="Close"
              className="absolute top-[max(0.75rem,env(safe-area-inset-top,0px))] right-[max(0.75rem,env(safe-area-inset-right,0px))] inline-flex size-7 items-center justify-center rounded-md text-fg-muted transition-colors duration-fast hover:bg-fg/6 hover:text-fg focus-ring"
            >
              <X className="size-4" />
            </BaseDialog.Close>
          )}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </OverlayLayerContext>
  );
}

/** A Dialog that slides in from an edge. Same anatomy as `Dialog`, with `side` on `Content`. */
export const Sheet = {
  Root: Dialog.Root,
  Trigger: Dialog.Trigger,
  Content,
  Header: Dialog.Header,
  Footer: Dialog.Footer,
  Title: Dialog.Title,
  Description: Dialog.Description,
  Close: Dialog.Close,
};
